﻿<!--
  StickyHowItWorks — sticky-scroll reveal. Left: a sticky "applicant dossier"
  visual that swaps to match the active step. Right: the steps, which dim until
  active. Active step tracked by an IntersectionObserver crossing viewport center.
  SSR-safe (observer wired in onMounted). No glow — hairline borders only.
-->
<template>
  <div class="hiw">
    <!-- Sticky visual (lg+) -->
    <div class="hiw-visual" aria-hidden="true">
      <div class="hiw-stickywrap">
        <div class="hiw-panel">
          <!-- Step 1: post a role — mirrors the real PostJobPage fields + buttons -->
          <div v-show="active === 0" class="hiw-card">
            <div class="hiw-card-top"><span class="hiw-dot"></span> New job listing</div>
            <div class="hiw-field">Senior Data Engineer</div>
            <div class="hiw-meta">Full-time · Remote · $140k–$180k / year</div>
            <div class="hiw-chips">
              <span class="hiw-chip hiw-chip--on">Visa sponsorship</span>
              <span class="hiw-chip hiw-chip--on">OPT eligible</span>
              <span class="hiw-chip hiw-chip--on">Accepts foreign credentials</span>
            </div>
            <div class="hiw-rows">
              <div class="hiw-row"><span>Education requirement</span><b>Bachelor's degree</b></div>
              <div class="hiw-row"><span>Screening questions</span><b>2 added</b></div>
            </div>
            <div class="hiw-cta">Preview listing</div>
          </div>
          <!-- Step 2: applicant — mirrors the real ApplicantDetailPanel -->
          <div v-show="active === 1" class="hiw-card">
            <div class="hiw-card-top"><span class="hiw-dot"></span> Applicant</div>
            <div class="hiw-appl">
              <span class="hiw-appl-name">Priya N.</span>
              <span class="hiw-badge">New</span>
              <span class="hiw-match">75% match</span>
            </div>
            <div class="hiw-rows">
              <div class="hiw-row"><span>Screening</span><b>3 / 3 passed</b></div>
              <div class="hiw-row"><span>Education</span><b>B.Tech, Computer Science · 2019–2023</b></div>
            </div>
          </div>
          <!-- Step 3: messaging — mirrors the real MessageThread -->
          <div v-show="active === 2" class="hiw-card">
            <div class="hiw-card-top"><span class="hiw-dot"></span> Conversation</div>
            <div class="hiw-msg hiw-msg--you">Hi Priya, your background fits our platform team. Free to chat Thursday?</div>
            <div class="hiw-msg hiw-msg--them">Yes. Thank you! 10am works.</div>
            <div class="hiw-replybox">Write a message…</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Steps -->
    <ol class="hiw-steps">
      <li
        v-for="(s, i) in steps"
        :key="s.n"
        ref="stepEls"
        class="hiw-step"
        :class="{ 'is-active': active === i }"
      >
        <div class="hiw-step-n">{{ s.n }}</div>
        <h3 class="hiw-step-title">{{ s.title }}</h3>
        <p class="hiw-step-body">{{ s.body }}</p>
      </li>
    </ol>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, onBeforeUnmount, type PropType } from 'vue';

interface Step { n: string; icon: string; title: string; body: string; }

export default defineComponent({
  name: 'StickyHowItWorks',
  props: {
    steps: { type: Array as PropType<Step[]>, required: true },
  },
  setup() {
    const stepEls = ref<HTMLElement[]>([]);
    const active = ref(0);
    let observer: IntersectionObserver | null = null;

    onMounted(() => {
      if (!('IntersectionObserver' in window)) return;
      observer = new IntersectionObserver(
        (entries) => {
          for (const e of entries) {
            if (e.isIntersecting) {
              const idx = stepEls.value.indexOf(e.target as HTMLElement);
              if (idx !== -1) active.value = idx;
            }
          }
        },
        // fire when a step crosses the vertical center of the viewport
        { rootMargin: '-50% 0px -50% 0px', threshold: 0 },
      );
      stepEls.value.forEach((el) => observer!.observe(el));
    });

    onBeforeUnmount(() => observer?.disconnect());

    return { stepEls, active };
  },
});
</script>

<style scoped>
.hiw {
  display: grid;
  gap: 48px;
}
@media (min-width: 1024px) {
  /* stretch (not start) so the left column fills the row height — a sticky child
     needs a tall containing block to have travel room as the steps scroll past. */
  .hiw { grid-template-columns: 1fr 1fr; gap: 64px; align-items: stretch; }
}

/* Sticky visual */
.hiw-visual { display: none; }
@media (min-width: 1024px) {
  .hiw-visual { display: block; }
}
/* Center the panel in the viewport so it sits beside whichever step is active.
   ~165px ≈ half the panel height, so its center lands near the viewport center. */
.hiw-stickywrap { position: sticky; top: calc(50vh - 165px); }
.hiw-panel {
  border: 1px solid rgba(var(--lp-fg), 0.12);
  background: var(--lp-panel, rgba(var(--lp-fg), 0.04));
  border-radius: 18px;
  padding: 24px;
  min-height: 290px;
}
.hiw-card { display: flex; flex-direction: column; gap: 12px; animation: hiw-fade 0.4s ease; }
@keyframes hiw-fade { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
.hiw-card-top {
  display: flex; align-items: center; gap: 8px;
  font-size: 0.75rem; letter-spacing: 0.06em; text-transform: uppercase;
  color: rgba(var(--lp-fg), 0.55);
}
.hiw-dot { width: 7px; height: 7px; border-radius: 9999px; background: rgba(var(--jb-primary-rgb), 1); }
.hiw-field {
  font-size: 1.0625rem; font-weight: 600; color: rgb(var(--lp-fg));
  border-bottom: 1px solid rgba(var(--lp-fg), 0.08); padding-bottom: 12px;
}
.hiw-field--muted { font-size: 0.875rem; font-weight: 400; color: rgba(var(--lp-fg), 0.55); border-bottom: 0; padding-bottom: 0; }
.hiw-chips { display: flex; flex-wrap: wrap; gap: 8px; }
.hiw-chip {
  font-size: 0.75rem; padding: 5px 10px; border-radius: 9999px;
  border: 1px dashed rgba(var(--lp-fg), 0.2); color: rgba(var(--lp-fg), 0.6);
}
.hiw-chip--on { border-style: solid; border-color: rgba(var(--jb-primary-rgb), 0.6); color: var(--jb-primary); }
.hiw-cta {
  margin-top: 4px; align-self: flex-start; font-size: 0.8125rem; font-weight: 600;
  padding: 8px 14px; border-radius: 10px; background: rgba(var(--jb-primary-rgb), 1); color: #fff;
}
.hiw-meta { font-size: 0.8125rem; color: rgba(var(--lp-fg), 0.6); }
.hiw-appl { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.hiw-appl-name { font-size: 1.0625rem; font-weight: 600; color: rgb(var(--lp-fg)); }
.hiw-badge {
  font-size: 0.7rem; padding: 3px 9px; border-radius: 9999px;
  background: rgba(var(--lp-fg), 0.08); color: rgba(var(--lp-fg), 0.72);
  border: 1px solid rgba(var(--lp-fg), 0.14);
}
.hiw-match {
  font-size: 0.7rem; font-weight: 700; padding: 3px 9px; border-radius: 9999px;
  background: rgba(var(--jb-primary-rgb), 0.18); color: var(--jb-primary);
  border: 1px solid rgba(var(--jb-primary-rgb), 0.5);
}
.hiw-flag {
  display: flex; align-items: center; gap: 7px;
  font-size: 0.8125rem; color: rgba(var(--lp-fg), 0.72); padding-top: 2px;
}
.hiw-replybox {
  margin-top: 4px; font-size: 0.8125rem; color: rgba(var(--lp-fg), 0.4);
  border: 1px solid rgba(var(--lp-fg), 0.12); border-radius: 10px; padding: 9px 12px;
}
.hiw-rows { display: flex; flex-direction: column; gap: 10px; }
.hiw-row {
  display: flex; justify-content: space-between; gap: 12px; font-size: 0.875rem;
  color: rgba(var(--lp-fg), 0.6);
}
.hiw-row b { color: rgb(var(--lp-fg)); font-weight: 600; }
.hiw-msg { font-size: 0.875rem; padding: 10px 14px; border-radius: 12px; max-width: 85%; }
.hiw-msg--them { align-self: flex-start; background: rgba(var(--lp-fg), 0.06); color: rgba(var(--lp-fg), 0.85); }
.hiw-msg--you { align-self: flex-end; background: rgba(var(--jb-primary-rgb), 0.85); color: #fff; }

/* Steps */
.hiw-steps { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 14vh; }
@media (max-width: 1023px) { .hiw-steps { gap: 40px; } }
/* Room below the last step so the centered sticky panel stays aligned with the
   final step before the column ends and it un-sticks (lg only — no sticky on mobile). */
@media (min-width: 1024px) { .hiw-steps { padding-bottom: 20vh; } }
.hiw-step { transition: opacity 0.3s ease; }
@media (min-width: 1024px) { .hiw-step { opacity: 0.35; } .hiw-step.is-active { opacity: 1; } }
.hiw-step-n {
  font-family: var(--jb-font-mono, ui-monospace, monospace);
  font-size: 0.875rem; letter-spacing: 0.1em; color: rgba(var(--jb-primary-rgb), 1);
}
.hiw-step-title { margin-top: 10px; font-size: 1.5rem; font-weight: 700; color: rgb(var(--lp-fg)); line-height: 1.2; }
.hiw-step-body { margin-top: 12px; font-size: 0.9375rem; line-height: 1.6; color: rgba(var(--lp-fg), 0.62); max-width: 48ch; }

@media (prefers-reduced-motion: reduce) {
  .hiw-card { animation: none; }
  .hiw-step { opacity: 1 !important; }
}
</style>
